import { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

const PARTICLE_COUNT = 120;
const CONNECTION_DIST = 180;
const MOUSE_DIST = 250;

const Canvas = styled('canvas')({
  position: 'fixed',
  inset: 0,
  zIndex: 0,
  width: '100%',
  height: '100%',
});

interface ParticleData {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  isGreen: boolean;
}

function createParticle(width: number, height: number): ParticleData {
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.4,
    vy: (Math.random() - 0.5) * 0.4,
    radius: Math.random() * 2.2 + 0.8,
    isGreen: Math.random() < 0.5,
  };
}

/* eslint-disable no-param-reassign */
function updateParticle(p: ParticleData, width: number, height: number, mouseX: number | null, mouseY: number | null) {
  p.x += p.vx;
  p.y += p.vy;
  if (p.x < 0 || p.x > width) p.vx *= -1;
  if (p.y < 0 || p.y > height) p.vy *= -1;

  if (mouseX !== null && mouseY !== null) {
    const dx = p.x - mouseX;
    const dy = p.y - mouseY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < MOUSE_DIST) {
      const force = ((MOUSE_DIST - dist) / MOUSE_DIST) * 0.015;
      p.vx += (dx / dist) * force;
      p.vy += (dy / dist) * force;
    }
  }
  p.vx *= 0.999;
  p.vy *= 0.999;
}
/* eslint-enable no-param-reassign */

function drawParticle(ctx: CanvasRenderingContext2D, p: ParticleData) {
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
  ctx.fillStyle = p.isGreen ? 'rgba(0, 171, 85, 0.9)' : 'rgba(145, 158, 171, 0.45)';
  ctx.fill();

  if (p.isGreen) {
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.radius + 6, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 171, 85, 0.15)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.radius + 12, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 171, 85, 0.05)';
    ctx.fill();
  }
}

function drawConnections(
  ctx: CanvasRenderingContext2D,
  particles: ParticleData[],
  mouseX: number | null,
  mouseY: number | null,
) {
  for (let i = 0; i < particles.length; i += 1) {
    for (let j = i + 1; j < particles.length; j += 1) {
      const dx = particles[i].x - particles[j].x;
      const dy = particles[i].y - particles[j].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < CONNECTION_DIST) {
        const opacity = (1 - dist / CONNECTION_DIST) * 0.25;
        const isGreenLine = particles[i].isGreen || particles[j].isGreen;
        ctx.beginPath();
        ctx.moveTo(particles[i].x, particles[i].y);
        ctx.lineTo(particles[j].x, particles[j].y);
        ctx.strokeStyle = isGreenLine ? `rgba(0, 171, 85, ${opacity * 2})` : `rgba(145, 158, 171, ${opacity})`;
        ctx.lineWidth = isGreenLine ? 1.0 : 0.6;
        ctx.stroke();
      }
    }
    if (mouseX !== null && mouseY !== null) {
      const dx = particles[i].x - mouseX;
      const dy = particles[i].y - mouseY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < MOUSE_DIST) {
        const opacity = (1 - dist / MOUSE_DIST) * 0.45;
        ctx.beginPath();
        ctx.moveTo(particles[i].x, particles[i].y);
        ctx.lineTo(mouseX, mouseY);
        ctx.strokeStyle = `rgba(0, 171, 85, ${opacity})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
    }
  }
}

export default function NetworkBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<ParticleData[]>([]);
  const mouseRef = useRef<{ x: number | null; y: number | null }>({ x: null, y: null });
  const animFrameRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => createParticle(canvas.width, canvas.height));

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { x: mx, y: my } = mouseRef.current;
      particlesRef.current.forEach((p) => {
        updateParticle(p, canvas.width, canvas.height, mx, my);
        drawParticle(ctx, p);
      });
      drawConnections(ctx, particlesRef.current, mx, my);
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return <Canvas ref={canvasRef} />;
}
